import { isImageGraphic } from '../symbols/model';
import { EMPTY_SYMBOL_CATALOGUE, type PreparedSymbolCatalogue } from '../symbols/images';
import { resolveSymbolImage, markerPresentation, symbolDisplayLongEdge } from '../symbols/scene';
import { taperedLeaderPolygons } from './callout';
import { UI_ACCENT_COLOUR } from '../styles/uiTokens';
import type { Feature, Polygon, MultiPolygon } from 'geojson';
import { presentationDimension, presentationScale } from '../map/presentation';
import { annotationRenderProxy } from './elevation';
import { booleanIntersects } from '@turf/boolean-intersects';
import { area } from '@turf/area';
import type { AddLayerObject, FilterSpecification, SourceSpecification } from 'maplibre-gl';

import { mapLibreLabelFontStack } from '../map/mapLibreLabelFonts';
import type { MapLibreRenderImage } from '../map/mapLibreRenderPlan';
import type { Annotation, AnnotationCoordinate, AnnotationLayer } from './model';
import type {
  FeatureProperties,
  JsonValue,
  SupportedGeometry,
  VectorFeatureCollection,
} from '../data/model';
import { ANNOTATION_FONT_FAMILIES, hasModernAnnotationStyle } from './typography';
import {
  annotationAreaRings,
  annotationAreaParts,
  annotationAreaGeometry,
  endpointMarkerGeometry,
  ellipseHandles,
  ellipseRing,
  lastDistinctSegment,
  polygonMidpoints,
  rectangleRenderRing,
  roundedRectanglePoints,
} from './geometry';
import {
  calloutLeaderCoordinates,
  estimateTextBoxLayout,
  textBoxBounds,
  type AnnotationScreenProjection,
} from './textLayout';

export const ANNOTATION_SOURCE_ID = 'dropmap-annotations';
export const FOCUS_AREA_SOURCE_ID = 'dropmap-focus-area-mask';

export interface AnnotationRenderPlan {
  readonly sources: readonly {
    readonly id: string;
    readonly data: VectorFeatureCollection;
    readonly definition: SourceSpecification;
  }[];
  readonly images: readonly MapLibreRenderImage[];
  readonly focusBasemapLayers: readonly AddLayerObject[];
  readonly focusDataLayers: readonly AddLayerObject[];
  readonly layers: readonly AddLayerObject[];
  readonly selectableLayerIds: readonly string[];
}

const emptyCollection = (): VectorFeatureCollection => ({
  type: 'FeatureCollection',
  features: [],
});
const filter = (layerId: string, kind: string, dashed?: boolean): FilterSpecification => {
  const expression = [
    'all',
    ['==', ['get', 'annotationLayerId'], layerId],
    ['==', ['get', 'kind'], kind],
  ];
  if (dashed !== undefined) expression.push(['==', ['get', 'dashed'], dashed] as never);
  return expression as unknown as FilterSpecification;
};

function feature(
  geometry: SupportedGeometry,
  layer: AnnotationLayer,
  annotation: Annotation,
  kind: string,
  extra: Record<string, JsonValue> = {},
): Feature<SupportedGeometry, FeatureProperties> {
  return {
    type: 'Feature',
    geometry,
    properties: {
      annotationLayerId: layer.id,
      annotationId: annotation.id,
      annotationType: annotation.type,
      kind,
      ...extra,
    },
  };
}

function endpointRenderGeometry(
  coordinates: readonly AnnotationCoordinate[],
  style: Extract<Annotation, { type: 'line' }>['style']['endStyle'],
  lineWidth: number,
  size: number,
  projection?: AnnotationScreenProjection,
): {
  readonly shaft: readonly AnnotationCoordinate[];
  readonly marker:
    | { readonly kind: 'circle'; readonly centre: AnnotationCoordinate; readonly radius: number }
    | { readonly kind: 'polygon'; readonly ring: readonly AnnotationCoordinate[] };
} | null {
  if (projection) {
    const geometry = endpointMarkerGeometry(
      coordinates.map(projection.project),
      style,
      lineWidth,
      size,
    );
    if (!geometry) return null;
    const marker =
      geometry.marker.kind === 'circle'
        ? {
            kind: 'circle' as const,
            centre: projection.unproject(geometry.marker.centre),
            radius: geometry.marker.radius,
          }
        : (() => {
            const ring = geometry.marker.points.map(projection.unproject);
            return { kind: 'polygon' as const, ring: [...ring, ring[0]!] };
          })();
    return { shaft: geometry.shaft.map(projection.unproject), marker };
  }
  // Renderer plans used without a live projection still retain valid directional geometry.
  const tip = coordinates.at(-1);
  if (!tip) return null;
  const latitudeScale = Math.max(0.1, Math.cos((tip[1] * Math.PI) / 180));
  const scale = 0.00001 * size;
  const points = coordinates.map(([longitude, latitude]) => ({
    x: longitude * latitudeScale,
    y: -latitude,
  }));
  const segment = lastDistinctSegment(points, 1e-10);
  if (!segment) return null;
  const geometry = endpointMarkerGeometry(points, style, lineWidth * 0.00001, scale, 1e-10);
  if (!geometry) return null;
  const marker =
    geometry.marker.kind === 'circle'
      ? {
          kind: 'circle' as const,
          centre: [
            geometry.marker.centre.x / latitudeScale,
            -geometry.marker.centre.y,
          ] as AnnotationCoordinate,
          radius: size / 2,
        }
      : (() => {
          const ring = geometry.marker.points.map(
            ({ x, y }) => [x / latitudeScale, -y] as AnnotationCoordinate,
          );
          return { kind: 'polygon' as const, ring: [...ring, ring[0]!] };
        })();
  return {
    shaft: geometry.shaft.map(({ x, y }) => [x / latitudeScale, -y] as AnnotationCoordinate),
    marker,
  };
}

function rectangleDisplayRing(
  annotation: Extract<Annotation, { type: 'rectangle' }>,
  projection?: AnnotationScreenProjection,
): readonly AnnotationCoordinate[] {
  const ring = rectangleRenderRing(annotation.coordinates, annotation.rotationDegrees);
  if (!projection || annotation.style.cornerRadius <= 0) return ring;
  return roundedRectanglePoints(ring.map(projection.project), annotation.style.cornerRadius).map(
    projection.unproject,
  );
}

function screenBoxRing(
  position: AnnotationCoordinate,
  text: string,
  style: Extract<Annotation, { type: 'text' | 'callout' }>['style'],
  projection: AnnotationScreenProjection,
): readonly AnnotationCoordinate[] {
  const bounds = textBoxBounds(projection.project(position), text, style);
  return [
    projection.unproject({ x: bounds.left, y: bounds.top }),
    projection.unproject({ x: bounds.right, y: bounds.top }),
    projection.unproject({ x: bounds.right, y: bounds.bottom }),
    projection.unproject({ x: bounds.left, y: bounds.bottom }),
    projection.unproject({ x: bounds.left, y: bounds.top }),
  ];
}

/** Complement of all parts, including a part nested inside another part's hole. */
function outsideFocusGeometry(
  annotation: Extract<Annotation, { type: 'focus-area' }>,
): Polygon | MultiPolygon {
  const parts = annotationAreaParts(annotation);
  const holes = parts.flatMap((part) =>
    (part.holes ?? []).map((ring) => ({
      type: 'Polygon' as const,
      coordinates: [ring.map((point) => [...point])],
    })),
  );
  const parents = parts.map((part) => {
    let parent = -1,
      smallest = Infinity;
    holes.forEach((hole, index) => {
      if (booleanIntersects(hole, { type: 'Point', coordinates: [...part.coordinates[0]!] })) {
        const size = area(hole);
        if (size < smallest) {
          parent = index;
          smallest = size;
        }
      }
    });
    return parent;
  });
  const world = [
    [-180, -85.051129],
    [180, -85.051129],
    [180, 85.051129],
    [-180, 85.051129],
    [-180, -85.051129],
  ];
  const childRings = (parent: number) =>
    parts
      .filter((_, index) => parents[index] === parent)
      .map((part) => [...part.coordinates].reverse().map((point) => [...point]));
  const polygons = [
    [world, ...childRings(-1)],
    ...holes.map((hole, index) => [hole.coordinates[0]!, ...childRings(index)]),
  ];
  return polygons.length === 1
    ? { type: 'Polygon', coordinates: polygons[0]! }
    : { type: 'MultiPolygon', coordinates: polygons };
}

function rgba(hex: string, alpha = 255): readonly [number, number, number, number] {
  return [
    Number.parseInt(hex.slice(1, 3), 16),
    Number.parseInt(hex.slice(3, 5), 16),
    Number.parseInt(hex.slice(5, 7), 16),
    alpha,
  ];
}

function markerImage(annotation: Extract<Annotation, { type: 'marker' }>): MapLibreRenderImage {
  const size = 64;
  const data = new Uint8Array(size * size * 4);
  const centre = (size - 1) / 2;
  const outer = 29;
  const inner = 24;
  const fill = rgba(annotation.style.fillColour);
  const outline = rgba(annotation.style.outlineColour);
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const dx = Math.abs(x - centre);
      const dy = Math.abs(y - centre);
      const inside =
        annotation.style.shape === 'circle'
          ? Math.hypot(dx, dy) <= outer
          : annotation.style.shape === 'square'
            ? Math.max(dx, dy) <= outer * 0.82
            : dx + dy <= outer * 1.15;
      const insideFill =
        annotation.style.shape === 'circle'
          ? Math.hypot(dx, dy) <= inner
          : annotation.style.shape === 'square'
            ? Math.max(dx, dy) <= inner * 0.82
            : dx + dy <= inner * 1.15;
      if (!inside) continue;
      const pixel = insideFill ? fill : outline;
      const offset = (y * size + x) * 4;
      data.set(pixel, offset);
    }
  }
  return { id: `dropmap-marker-${annotation.id}`, width: size, height: size, data, pixelRatio: 2 };
}

// Camera-only updates reuse immutable backgrounds; bound transient retention across projects/exports.
const textImageCache = new Map<string, MapLibreRenderImage>();
let textImageBytes = 0;
function textBackgroundImage(
  annotation: Extract<Annotation, { type: 'text' | 'callout' }>,
): MapLibreRenderImage {
  const style = annotation.style;
  const box = estimateTextBoxLayout(annotation.text, style);
  const key = JSON.stringify([
    style.backgroundColour,
    style.backgroundOpacity,
    style.borderColour,
    style.borderWidth,
    style.cornerRadius,
    box.width,
    box.height,
  ]);
  const cached = textImageCache.get(key);
  // Glyphs are drawn separately. Identical backgrounds share pixels, not annotation identity.
  if (cached)
    return cached.id === `dropmap-text-background-${annotation.id}`
      ? cached
      : { ...cached, id: `dropmap-text-background-${annotation.id}` };
  const image = createTextBackgroundImage(annotation);
  while (
    textImageCache.size &&
    (textImageBytes + image.data.byteLength > 8 * 1024 * 1024 || textImageCache.size >= 64)
  ) {
    const first = textImageCache.keys().next().value!;
    textImageBytes -= textImageCache.get(first)!.data.byteLength;
    textImageCache.delete(first);
  }
  textImageCache.set(key, image);
  textImageBytes += image.data.byteLength;
  return image;
}

function createTextBackgroundImage(
  annotation: Extract<Annotation, { type: 'text' | 'callout' }>,
): MapLibreRenderImage {
  const layout = estimateTextBoxLayout(annotation.text, annotation.style);
  const imageScale = Math.min(4, 1024 / Math.max(layout.width, layout.height));
  const width = Math.max(1, Math.ceil(layout.width * imageScale));
  const height = Math.max(1, Math.ceil(layout.height * imageScale));
  const data = new Uint8Array(width * height * 4);
  const fill = rgba(
    annotation.style.backgroundColour,
    Math.round(annotation.style.backgroundOpacity * 255),
  );
  const border = rgba(annotation.style.borderColour, annotation.style.borderWidth > 0 ? 255 : 0);
  const borderWidth = annotation.style.borderWidth * imageScale;
  const radius = Math.min(annotation.style.cornerRadius * imageScale, width / 2, height / 2);
  const insideRoundedRectangle = (x: number, y: number, inset: number): boolean => {
    const innerWidth = width - inset * 2;
    const innerHeight = height - inset * 2;
    if (innerWidth <= 0 || innerHeight <= 0) return false;
    const localX = x - inset;
    const localY = y - inset;
    if (localX < 0 || localY < 0 || localX > innerWidth || localY > innerHeight) return false;
    const innerRadius = Math.max(0, radius - inset);
    const dx = Math.max(0, innerRadius - Math.min(localX, innerWidth - localX));
    const dy = Math.max(0, innerRadius - Math.min(localY, innerHeight - localY));
    return Math.hypot(dx, dy) <= innerRadius;
  };
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (radius > 0) {
        let outerCoverage = 0;
        let innerCoverage = 0;
        for (const sampleY of [0.25, 0.75])
          for (const sampleX of [0.25, 0.75]) {
            if (insideRoundedRectangle(x + sampleX, y + sampleY, 0)) outerCoverage += 0.25;
            if (insideRoundedRectangle(x + sampleX, y + sampleY, borderWidth))
              innerCoverage += 0.25;
          }
        if (outerCoverage === 0) continue;
        const borderCoverage = Math.max(0, outerCoverage - innerCoverage);
        const fillAlpha = (fill[3] / 255) * innerCoverage;
        const borderAlpha = (border[3] / 255) * borderCoverage;
        const alpha = fillAlpha + borderAlpha;
        if (alpha === 0) continue;
        const offset = (y * width + x) * 4;
        data[offset] = Math.round((fill[0] * fillAlpha + border[0] * borderAlpha) / alpha);
        data[offset + 1] = Math.round((fill[1] * fillAlpha + border[1] * borderAlpha) / alpha);
        data[offset + 2] = Math.round((fill[2] * fillAlpha + border[2] * borderAlpha) / alpha);
        data[offset + 3] = Math.round(alpha * 255);
        continue;
      }
      data.set(
        x < borderWidth ||
          y < borderWidth ||
          width - x - 1 < borderWidth ||
          height - y - 1 < borderWidth
          ? border
          : fill,
        (y * width + x) * 4,
      );
    }
  }
  return {
    id: `dropmap-text-background-${annotation.id}`,
    width,
    height,
    data,
    pixelRatio: imageScale,
  };
}

function scaleAnnotationForRender(
  annotation: Annotation,
  requestedScale: number,
  presentation = 1,
): Annotation {
  const scale = Number.isFinite(requestedScale) && requestedScale > 0 ? requestedScale : 1;
  const dimension = (value: number, minimum = 0) =>
    presentationDimension(value, presentation, minimum) * scale;
  if (scale === 1 && presentation === 1) return annotation;
  switch (annotation.type) {
    case 'marker':
      return {
        ...annotation,
        style: { ...annotation.style, size: dimension(annotation.style.size, 1) },
      };
    case 'line':
      return {
        ...annotation,
        style: { ...annotation.style, width: dimension(annotation.style.width, 0.1) },
      };
    case 'polygon':
    case 'ellipse':
      return {
        ...annotation,
        style: { ...annotation.style, outlineWidth: dimension(annotation.style.outlineWidth, 0.1) },
      };
    case 'rectangle':
      return {
        ...annotation,
        style: {
          ...annotation.style,
          outlineWidth: dimension(annotation.style.outlineWidth, 0.1),
          cornerRadius: dimension(annotation.style.cornerRadius),
        },
      };
    case 'text':
      return {
        ...annotation,
        style: {
          ...annotation.style,
          fontSize: dimension(annotation.style.fontSize, 2),
          ...(annotation.style.boxHeight !== undefined
            ? {
                boxHeight:
                  (annotation.style.boxHeight * dimension(annotation.style.fontSize, 2)) /
                  annotation.style.fontSize,
              }
            : {}),
          ...(annotation.style.textHaloWidth !== undefined
            ? { textHaloWidth: dimension(annotation.style.textHaloWidth) }
            : {}),
          borderWidth: dimension(annotation.style.borderWidth, 0.1),
          padding: dimension(annotation.style.padding),
          boxWidth:
            annotation.style.boxWidth *
            (dimension(annotation.style.fontSize, 2) / annotation.style.fontSize),
          cornerRadius: dimension(annotation.style.cornerRadius),
        },
      };
    case 'callout':
      return {
        ...annotation,
        style: {
          ...annotation.style,
          fontSize: dimension(annotation.style.fontSize, 2),
          ...(annotation.style.boxHeight !== undefined
            ? {
                boxHeight:
                  (annotation.style.boxHeight * dimension(annotation.style.fontSize, 2)) /
                  annotation.style.fontSize,
              }
            : {}),
          ...(annotation.style.textHaloWidth !== undefined
            ? { textHaloWidth: dimension(annotation.style.textHaloWidth) }
            : {}),
          borderWidth: dimension(annotation.style.borderWidth, 0.1),
          padding: dimension(annotation.style.padding),
          boxWidth:
            annotation.style.boxWidth *
            (dimension(annotation.style.fontSize, 2) / annotation.style.fontSize),
          cornerRadius: dimension(annotation.style.cornerRadius),
          leaderWidth: dimension(annotation.style.leaderWidth, 0.1),
        },
      };
    case 'focus-area':
      return {
        ...annotation,
        style: {
          ...annotation.style,
          boundaryWidth: dimension(annotation.style.boundaryWidth, 0.1),
        },
      };
  }
}

export function createAnnotationRenderPlan(
  annotationLayers: readonly AnnotationLayer[],
  selectedAnnotationId?: string | null,
  projection?: AnnotationScreenProjection,
  requestedActiveFocusId?: string | null,
  annotationRenderScale = 1,
  requestedPresentationScale = 1,
  symbolImages: PreparedSymbolCatalogue = EMPTY_SYMBOL_CATALOGUE,
): AnnotationRenderPlan {
  const presentation = presentationScale(requestedPresentationScale);
  const data = emptyCollection();
  const focusData = emptyCollection();
  const images: MapLibreRenderImage[] = [];
  // Corrupt/legacy state cannot stack masks: the topmost visible focus wins deterministically.
  const activeFocusId =
    requestedActiveFocusId === undefined
      ? annotationLayers
          .find(
            (layer) =>
              layer.visible &&
              layer.annotations.some((item) => item.type === 'focus-area' && item.visible),
          )
          ?.annotations.find((item) => item.type === 'focus-area' && item.visible)?.id
      : requestedActiveFocusId;
  for (const layer of annotationLayers) {
    if (!layer.visible) continue;
    for (const authoredAnnotation of layer.annotations) {
      if (!authoredAnnotation.visible) continue;
      const renderAnnotation = projection
        ? annotationRenderProxy(authoredAnnotation, projection)
        : authoredAnnotation;
      if (!renderAnnotation) continue;
      const annotation = scaleAnnotationForRender(
        renderAnnotation,
        annotationRenderScale,
        presentation,
      );
      const authoredEndpointWidth =
        authoredAnnotation.type === 'line'
          ? authoredAnnotation.style.width
          : authoredAnnotation.type === 'callout'
            ? authoredAnnotation.style.leaderWidth
            : 0;
      // Scale the existing physical endpoint as a whole, preserving its 12 px minimum at factor one.
      const density =
        Number.isFinite(annotationRenderScale) && annotationRenderScale > 0
          ? annotationRenderScale
          : 1;
      const endpointSize =
        (authoredAnnotation.type === 'callout' && authoredAnnotation.style.leaderStyle === 'tapered'
          ? Math.max(4, Math.min(6, authoredEndpointWidth)) * density
          : Math.max(12, authoredEndpointWidth * density * 5)) * presentation;
      switch (annotation.type) {
        case 'marker': {
          const pictogramStyle = markerPresentation(
            authoredAnnotation.type === 'marker' ? authoredAnnotation.style : annotation.style,
          );
          const image = isImageGraphic(annotation.style.shape)
            ? resolveSymbolImage(
                symbolImages,
                annotation.style.shape,
                annotation.style.fillColour,
                annotation.style.tint,
                pictogramStyle,
              )
            : markerImage(annotation);
          const logicalEdge = Math.max(image.width, image.height) / image.pixelRatio;
          data.features.push(
            feature(
              { type: 'Point', coordinates: [...annotation.position] },
              layer,
              annotation,
              'marker',
              {
                image: image.id,
                size: annotation.style.size,
                imageScale:
                  (isImageGraphic(annotation.style.shape)
                    ? symbolDisplayLongEdge(image, annotation.style.size)
                    : annotation.style.size) / logicalEdge,
                rotation: isImageGraphic(annotation.style.shape)
                  ? (annotation.style.rotationDegrees ?? 0)
                  : 0,
                anchor: isImageGraphic(annotation.style.shape)
                  ? (annotation.style.anchor ?? 'center')
                  : 'center',
                opacity:
                  isImageGraphic(annotation.style.shape) && annotation.style.backing?.enabled
                    ? 1
                    : annotation.style.opacity,
              },
            ),
          );
          images.push(image);
          break;
        }
        case 'line': {
          const endpoint =
            annotation.style.endStyle !== 'none'
              ? endpointRenderGeometry(
                  annotation.coordinates,
                  annotation.style.endStyle,
                  annotation.style.width,
                  endpointSize,
                  projection,
                )
              : null;
          data.features.push(
            feature(
              {
                type: 'LineString',
                coordinates: (endpoint?.shaft ?? annotation.coordinates).map((coordinate) => [
                  ...coordinate,
                ]),
              },
              layer,
              annotation,
              'line',
              {
                colour: annotation.style.colour,
                width: annotation.style.width,
                opacity: annotation.style.opacity,
                dashed: annotation.style.dash === 'dashed',
              },
            ),
          );
          if (endpoint) {
            data.features.push(
              feature(
                endpoint.marker.kind === 'circle'
                  ? { type: 'Point', coordinates: [...endpoint.marker.centre] }
                  : {
                      type: 'Polygon',
                      coordinates: [endpoint.marker.ring.map((coordinate) => [...coordinate])],
                    },
                layer,
                annotation,
                endpoint.marker.kind === 'circle' ? 'endpoint-circle' : 'arrowhead',
                {
                  fillColour: annotation.style.colour,
                  fillOpacity: annotation.style.opacity,
                  ...(endpoint.marker.kind === 'circle' ? { radius: endpoint.marker.radius } : {}),
                },
              ),
            );
          }
          break;
        }
        case 'polygon':
        case 'rectangle':
        case 'ellipse': {
          const areaCoordinates =
            annotation.type === 'ellipse'
              ? ellipseRing(annotation.bounds, annotation.circleLocked, annotation.rotationDegrees)
              : annotation.type === 'rectangle'
                ? rectangleDisplayRing(annotation, projection)
                : annotation.coordinates;
          data.features.push(
            feature(
              annotation.type === 'polygon'
                ? annotationAreaGeometry(annotation)
                : {
                    type: 'Polygon',
                    coordinates: [areaCoordinates.map((coordinate) => [...coordinate])],
                  },
              layer,
              annotation,
              'area',
              {
                fillColour: annotation.style.fillColour,
                fillOpacity: annotation.style.fillOpacity,
                outlineColour: annotation.style.outlineColour,
                outlineWidth: annotation.style.outlineWidth,
                dashed: annotation.style.outlineDash === 'dashed',
              },
            ),
          );
          break;
        }
        case 'text': {
          const layout = estimateTextBoxLayout(annotation.text, annotation.style);
          const textRadialOffset =
            annotation.style.alignment === 'left'
              ? layout.inset / annotation.style.fontSize
              : annotation.style.alignment === 'right'
                ? layout.inset / annotation.style.fontSize
                : 0;
          data.features.push(
            feature(
              { type: 'Point', coordinates: [...annotation.position] },
              layer,
              annotation,
              'text-background',
              {
                image: `dropmap-text-background-${annotation.id}`,
                alignment: annotation.style.alignment,
                billboardScale: hasModernAnnotationStyle(annotation.style)
                  ? (projection?.billboardScaleAt?.(annotation.position) ?? 1)
                  : 1,
              },
            ),
          );
          data.features.push(
            feature(
              { type: 'Point', coordinates: [...annotation.position] },
              layer,
              annotation,
              'text',
              {
                text: layout.lines.join('\n'),
                colour: annotation.style.colour,
                fontSize: annotation.style.fontSize,
                fontVariant: `${annotation.style.fontWeight ?? 400}-${annotation.style.fontStyle ?? 'normal'}`,
                haloWidth: annotation.style.textHaloWidth ?? 0,
                haloColour: annotation.style.textHaloColour ?? '#ffffff',
                billboardScale: hasModernAnnotationStyle(annotation.style)
                  ? (projection?.billboardScaleAt?.(annotation.position) ?? 1)
                  : 1,
                fontFamily: annotation.style.fontFamily,
                alignment: annotation.style.alignment,
                textRadialOffset,
              },
            ),
          );
          images.push(textBackgroundImage(annotation));
          break;
        }
        case 'callout': {
          const billboardScale = projection?.billboardScaleAt?.(annotation.textPosition) ?? 1;
          const leaderCoordinates = calloutLeaderCoordinates(
            annotation.target,
            annotation.textPosition,
            annotation.text,
            annotation.style,
            projection,
            annotation.keepLeaderVerticalOnScreen,
            annotation.style.leaderStyle === 'tapered' ? annotation.style.borderWidth / 2 : 0,
          );
          const endpoint =
            annotation.style.targetStyle !== 'none'
              ? endpointRenderGeometry(
                  leaderCoordinates,
                  annotation.style.targetStyle,
                  annotation.style.leaderWidth,
                  endpointSize,
                  projection,
                )
              : null;
          if (annotation.style.leaderStyle === 'tapered' && projection) {
            const points = (endpoint?.shaft ?? leaderCoordinates).map((coordinate) =>
              projection.project(coordinate),
            );
            for (const ring of taperedLeaderPolygons(
              points[0]!,
              points[1]!,
              annotation.style.leaderWidth,
              annotation.style.leaderDash === 'dashed',
            )) {
              const coordinates = ring.map((point) => projection.unproject(point));
              if (coordinates.every((coordinate) => coordinate.every(Number.isFinite)))
                data.features.push(
                  feature(
                    {
                      type: 'Polygon',
                      coordinates: [coordinates.map((coordinate) => [...coordinate])],
                    },
                    layer,
                    annotation,
                    'tapered-leader',
                    {
                      fillColour: annotation.style.leaderColour,
                      fillOpacity: annotation.style.leaderOpacity ?? 1,
                    },
                  ),
                );
            }
          } else {
            data.features.push(
              feature(
                {
                  type: 'LineString',
                  coordinates: (endpoint?.shaft ?? leaderCoordinates).map((coordinate) => [
                    ...coordinate,
                  ]),
                },
                layer,
                annotation,
                'leader',
                {
                  colour: annotation.style.leaderColour,
                  width: annotation.style.leaderWidth,
                  opacity: annotation.style.leaderOpacity ?? 1,
                  dashed: annotation.style.leaderDash === 'dashed',
                },
              ),
            );
          }
          if (endpoint) {
            data.features.push(
              feature(
                endpoint.marker.kind === 'circle'
                  ? { type: 'Point', coordinates: [...endpoint.marker.centre] }
                  : {
                      type: 'Polygon',
                      coordinates: [endpoint.marker.ring.map((coordinate) => [...coordinate])],
                    },
                layer,
                annotation,
                endpoint.marker.kind === 'circle' ? 'endpoint-circle' : 'arrowhead',
                {
                  fillColour: annotation.style.leaderColour,
                  fillOpacity: annotation.style.leaderOpacity ?? 1,
                  ...(endpoint.marker.kind === 'circle' ? { radius: endpoint.marker.radius } : {}),
                },
              ),
            );
          }
          const layout = estimateTextBoxLayout(annotation.text, annotation.style);
          const textRadialOffset =
            annotation.style.alignment === 'left'
              ? layout.inset / annotation.style.fontSize
              : annotation.style.alignment === 'right'
                ? layout.inset / annotation.style.fontSize
                : 0;
          data.features.push(
            feature(
              { type: 'Point', coordinates: [...annotation.textPosition] },
              layer,
              annotation,
              'text-background',
              {
                image: `dropmap-text-background-${annotation.id}`,
                alignment: annotation.style.alignment,
                billboardScale,
              },
            ),
          );
          data.features.push(
            feature(
              { type: 'Point', coordinates: [...annotation.textPosition] },
              layer,
              annotation,
              'text',
              {
                text: layout.lines.join('\n'),
                colour: annotation.style.colour,
                fontSize: annotation.style.fontSize,
                billboardScale,
                fontVariant: `${annotation.style.fontWeight ?? 400}-${annotation.style.fontStyle ?? 'normal'}`,
                haloWidth: annotation.style.textHaloWidth ?? 0,
                haloColour: annotation.style.textHaloColour ?? '#ffffff',
                fontFamily: annotation.style.fontFamily,
                alignment: annotation.style.alignment,
                textRadialOffset,
              },
            ),
          );
          images.push(textBackgroundImage(annotation));
          break;
        }
        case 'focus-area':
          data.features.push(
            feature(annotationAreaGeometry(annotation), layer, annotation, 'focus-hit-area'),
          );
          if (annotation.id === activeFocusId)
            focusData.features.push(
              feature(
                annotation.style.fadeDirection === 'outside'
                  ? outsideFocusGeometry(annotation)
                  : annotationAreaGeometry(annotation),
                layer,
                annotation,
                'focus-fade',
                {
                  fillColour: annotation.style.fadeColour,
                  fillOpacity: annotation.style.fadeOpacity,
                  scope: annotation.style.scope,
                },
              ),
            );
          if (annotation.style.boundaryVisible)
            data.features.push(
              feature(
                {
                  type: 'MultiLineString',
                  coordinates: annotationAreaRings(annotation).map((ring) =>
                    ring.map((coordinate) => [...coordinate]),
                  ),
                },
                layer,
                annotation,
                'focus-boundary',
                {
                  colour: annotation.style.boundaryColour,
                  width: annotation.style.boundaryWidth,
                  opacity: 1,
                  dashed: annotation.style.boundaryDash === 'dashed',
                },
              ),
            );
          break;
      }
      if (annotation.id !== selectedAnnotationId) continue;
      if (annotation.type === 'marker') {
        if (projection && isImageGraphic(annotation.style.shape)) {
          const image = resolveSymbolImage(
            symbolImages,
            annotation.style.shape,
            annotation.style.fillColour,
            annotation.style.tint,
            markerPresentation(
              authoredAnnotation.type === 'marker' ? authoredAnnotation.style : annotation.style,
            ),
          );
          const scale =
            symbolDisplayLongEdge(image, annotation.style.size) /
            Math.max(image.width, image.height);
          const w = (image.width * scale) / 2 + 3,
            h = (image.height * scale) / 2 + 3;
          const centre = projection.project(annotation.position);
          const angle = ((annotation.style.rotationDegrees ?? 0) * Math.PI) / 180;
          const bottom = annotation.style.anchor === 'bottom' ? h - 3 : 0;
          const ring = [
            [-w, -h - bottom],
            [w, -h - bottom],
            [w, h - bottom],
            [-w, h - bottom],
            [-w, -h - bottom],
          ].map(([x, y]) =>
            projection.unproject({
              x: centre.x + x! * Math.cos(angle) - y! * Math.sin(angle),
              y: centre.y + x! * Math.sin(angle) + y! * Math.cos(angle),
            }),
          );
          data.features.push(
            feature(
              { type: 'Polygon', coordinates: [ring.map((p) => [...p])] },
              layer,
              annotation,
              'edit-box',
            ),
          );
        } else {
          data.features.push(
            feature(
              { type: 'Point', coordinates: [...annotation.position] },
              layer,
              annotation,
              'edit-point',
              { size: annotation.style.size },
            ),
          );
        }
      } else if (
        annotation.type === 'line' ||
        annotation.type === 'polygon' ||
        annotation.type === 'rectangle' ||
        annotation.type === 'ellipse' ||
        annotation.type === 'focus-area'
      ) {
        const editCoordinates =
          annotation.type === 'ellipse'
            ? ellipseRing(annotation.bounds, annotation.circleLocked, annotation.rotationDegrees)
            : annotation.type === 'rectangle'
              ? rectangleDisplayRing(annotation, projection)
              : annotation.type === 'line' && annotation.style.endStyle !== 'none'
                ? (endpointRenderGeometry(
                    annotation.coordinates,
                    annotation.style.endStyle,
                    annotation.style.width,
                    Math.max(12, annotation.style.width * 5),
                    projection,
                  )?.shaft ?? annotation.coordinates)
                : annotation.coordinates;
        data.features.push(
          feature(
            {
              ...((annotation.type === 'polygon' || annotation.type === 'focus-area') &&
              (annotation.holes?.length || annotation.additionalParts?.length)
                ? {
                    type: 'MultiLineString' as const,
                    coordinates: annotationAreaRings(annotation).map((ring) =>
                      ring.map((coordinate) => [...coordinate]),
                    ),
                  }
                : {
                    type: 'LineString' as const,
                    coordinates: editCoordinates.map((coordinate) => [...coordinate]),
                  }),
            },
            layer,
            annotation,
            'edit-outline',
          ),
        );
        // Individual-part vertex editing is deferred; whole-object hit/movement stays enabled.
        if (
          (annotation.type === 'polygon' || annotation.type === 'focus-area') &&
          annotation.additionalParts?.length
        )
          continue;
        const vertices =
          annotation.type === 'ellipse'
            ? Object.entries(
                ellipseHandles(
                  annotation.bounds,
                  annotation.circleLocked,
                  annotation.rotationDegrees,
                ),
              ).map(([ellipseHandle, coordinate]) => ({ coordinate, ellipseHandle }))
            : annotation.type === 'rectangle'
              ? rectangleRenderRing(annotation.coordinates, annotation.rotationDegrees)
                  .slice(0, -1)
                  .map((coordinate, vertexIndex) => ({ coordinate, vertexIndex }))
              : (annotation.type === 'line'
                  ? annotation.coordinates
                  : annotation.coordinates.slice(0, -1)
                ).map((coordinate, vertexIndex) => ({ coordinate, vertexIndex }));
        vertices.forEach(({ coordinate, ...handle }) => {
          data.features.push(
            feature(
              { type: 'Point', coordinates: [...coordinate] },
              layer,
              annotation,
              'vertex-handle',
              handle,
            ),
          );
        });
        if (annotation.type === 'polygon' || annotation.type === 'focus-area')
          polygonMidpoints(annotation.coordinates).forEach(({ coordinate, insertIndex }) => {
            data.features.push(
              feature(
                { type: 'Point', coordinates: [...coordinate] },
                layer,
                annotation,
                'midpoint-handle',
                { insertIndex },
              ),
            );
          });
      } else if (projection) {
        const position = annotation.type === 'text' ? annotation.position : annotation.textPosition;
        const ring = screenBoxRing(position, annotation.text, annotation.style, projection);
        data.features.push(
          feature(
            { type: 'Polygon', coordinates: [ring.map((coordinate) => [...coordinate])] },
            layer,
            annotation,
            'edit-box',
          ),
        );
        const edgeTop = projection.project(
          annotation.style.alignment === 'right' ? ring[0]! : ring[1]!,
        );
        const edgeBottom = projection.project(
          annotation.style.alignment === 'right' ? ring[3]! : ring[2]!,
        );
        data.features.push(
          feature(
            {
              type: 'Point',
              coordinates: [
                ...projection.unproject({
                  x: edgeTop.x,
                  y: (edgeTop.y + edgeBottom.y) / 2,
                }),
              ],
            },
            layer,
            annotation,
            'text-resize-handle',
          ),
        );
        const bottom = projection.project(ring[2]!);
        const left = projection.project(ring[3]!);
        for (const [kind, point] of [
          ['text-height-handle', { x: (bottom.x + left.x) / 2, y: bottom.y }],
          ['text-size-handle', { x: edgeBottom.x, y: bottom.y }],
        ] as const)
          data.features.push(
            feature(
              { type: 'Point', coordinates: [...projection.unproject(point)] },
              layer,
              annotation,
              kind,
            ),
          );
        if (annotation.type === 'callout') {
          data.features.push(
            feature(
              { type: 'Point', coordinates: [...annotation.target] },
              layer,
              annotation,
              'target-handle',
            ),
            feature(
              { type: 'Point', coordinates: [...annotation.textPosition] },
              layer,
              annotation,
              'text-position-handle',
            ),
          );
        }
      }
    }
  }

  const focusLayer = (id: string, scope: 'basemap' | 'basemap-and-data'): AddLayerObject => ({
    id,
    type: 'fill',
    source: FOCUS_AREA_SOURCE_ID,
    filter: ['==', ['get', 'scope'], scope] as FilterSpecification,
    paint: { 'fill-color': ['get', 'fillColour'], 'fill-opacity': ['get', 'fillOpacity'] },
  });
  const focusBasemapLayers = focusData.features.length
    ? [focusLayer('dropmap-annotation-focus-basemap', 'basemap')]
    : [];
  const focusDataLayers = focusData.features.length
    ? [focusLayer('dropmap-annotation-focus-data', 'basemap-and-data')]
    : [];
  const layers: AddLayerObject[] = [];
  const selectableLayerIds: string[] = [];
  for (const layer of [...annotationLayers].reverse()) {
    if (!layer.visible) continue;
    const prefix = `dropmap-annotation-${layer.id}`;
    const layerStack: AddLayerObject[] = [];
    const addSelectable = (item: AddLayerObject) => {
      layerStack.push(item);
      selectableLayerIds.push(item.id);
    };
    addSelectable({
      id: `${prefix}-focus-hit-area`,
      type: 'fill',
      source: ANNOTATION_SOURCE_ID,
      filter: filter(layer.id, 'focus-hit-area'),
      paint: { 'fill-color': 'rgba(0, 0, 0, 0)', 'fill-opacity': 1 },
    });
    addSelectable({
      id: `${prefix}-area-fill`,
      type: 'fill',
      source: ANNOTATION_SOURCE_ID,
      filter: [
        'all',
        ['==', ['get', 'annotationLayerId'], layer.id],
        ['in', ['get', 'kind'], ['literal', ['area', 'tapered-leader']]],
      ],
      paint: { 'fill-color': ['get', 'fillColour'], 'fill-opacity': ['get', 'fillOpacity'] },
    });
    for (const dashed of [false, true])
      addSelectable({
        id: `${prefix}-area-outline-${dashed ? 'dashed' : 'solid'}`,
        type: 'line',
        source: ANNOTATION_SOURCE_ID,
        filter: filter(layer.id, 'area', dashed),
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': ['get', 'outlineColour'],
          'line-width': ['get', 'outlineWidth'],
          ...(dashed ? { 'line-dasharray': [3, 2] } : {}),
        },
      });
    for (const kind of ['line', 'leader', 'focus-boundary'])
      for (const dashed of [false, true])
        addSelectable({
          id: `${prefix}-${kind}-${dashed ? 'dashed' : 'solid'}`,
          type: 'line',
          source: ANNOTATION_SOURCE_ID,
          filter: filter(layer.id, kind, dashed),
          layout: {
            'line-cap': kind === 'focus-boundary' ? 'round' : 'butt',
            'line-join': 'round',
          },
          paint: {
            'line-color': ['get', 'colour'],
            'line-width': ['get', 'width'],
            'line-opacity': ['get', 'opacity'],
            ...(dashed ? { 'line-dasharray': [3, 2] } : {}),
          },
        });
    addSelectable({
      id: `${prefix}-arrowhead`,
      type: 'fill',
      source: ANNOTATION_SOURCE_ID,
      filter: filter(layer.id, 'arrowhead'),
      paint: {
        'fill-color': ['get', 'fillColour'],
        'fill-opacity': ['get', 'fillOpacity'],
      },
    });
    addSelectable({
      id: `${prefix}-endpoint-circle`,
      type: 'circle',
      source: ANNOTATION_SOURCE_ID,
      filter: filter(layer.id, 'endpoint-circle'),
      paint: {
        'circle-radius': ['get', 'radius'],
        'circle-color': ['get', 'fillColour'],
        'circle-opacity': ['get', 'fillOpacity'],
        'circle-pitch-alignment': 'viewport',
        'circle-pitch-scale': 'viewport',
      },
    });
    addSelectable({
      id: `${prefix}-marker`,
      type: 'symbol',
      source: ANNOTATION_SOURCE_ID,
      filter: filter(layer.id, 'marker'),
      layout: {
        'icon-image': ['get', 'image'],
        'icon-size': ['get', 'imageScale'],
        'icon-rotate': ['get', 'rotation'],
        'icon-anchor': ['get', 'anchor'],
        'icon-rotation-alignment': 'viewport',
        'icon-pitch-alignment': 'viewport',
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
      paint: { 'icon-opacity': ['get', 'opacity'] },
    });
    addSelectable({
      id: `${prefix}-text-background`,
      type: 'symbol',
      source: ANNOTATION_SOURCE_ID,
      filter: filter(layer.id, 'text-background'),
      layout: {
        'icon-image': ['get', 'image'],
        'icon-anchor': ['match', ['get', 'alignment'], 'left', 'left', 'right', 'right', 'center'],
        'icon-size': ['coalesce', ['get', 'billboardScale'], 1],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    });
    for (const font of ANNOTATION_FONT_FAMILIES.filter(
      (family) =>
        // Keep the four historical layer IDs stable; newer families stay demand-created.
        ['inter', 'source-sans-3', 'source-serif-4', 'roboto-condensed'].includes(family.id) ||
        layer.annotations.some(
          (annotation) =>
            (annotation.type === 'text' || annotation.type === 'callout') &&
            annotation.style.fontFamily === family.id,
        ),
    )) {
      const textLayer: AddLayerObject = {
        id: `${prefix}-text-${font.id}`,
        type: 'symbol',
        source: ANNOTATION_SOURCE_ID,
        filter: [
          'all',
          filter(layer.id, 'text'),
          ['==', ['get', 'fontFamily'], font.id],
        ] as unknown as FilterSpecification,
        layout: {
          'text-field': ['get', 'text'],
          'text-size': ['*', ['get', 'fontSize'], ['coalesce', ['get', 'billboardScale'], 1]],
          'text-max-width': 1000,
          'text-font': [
            'match',
            ['get', 'fontVariant'],
            ...([400, 500, 600, 700] as const).flatMap((weight) =>
              (font.italic ? (['normal', 'italic'] as const) : (['normal'] as const)).flatMap(
                (style) => [
                  `${weight}-${style}`,
                  [
                    'literal',
                    [
                      ...mapLibreLabelFontStack({
                        fontFamily: font.id,
                        fontWeight: weight,
                        fontStyle: style,
                      }),
                    ],
                  ],
                ],
              ),
            ),
            [
              'literal',
              [
                ...mapLibreLabelFontStack({
                  fontFamily: font.id,
                  fontWeight: 400,
                  fontStyle: 'normal',
                }),
              ],
            ],
          ] as unknown as import('maplibre-gl').ExpressionSpecification,
          'text-allow-overlap': true,
          'text-ignore-placement': true,
          'text-anchor': [
            'match',
            ['get', 'alignment'],
            'left',
            'left',
            'right',
            'right',
            'center',
          ],
          'text-justify': [
            'match',
            ['get', 'alignment'],
            'left',
            'left',
            'right',
            'right',
            'center',
          ],
          'text-radial-offset': ['get', 'textRadialOffset'],
        },
        paint: {
          'text-color': ['get', 'colour'],
          'text-halo-width': ['get', 'haloWidth'],
          'text-halo-color': ['get', 'haloColour'],
        },
      };
      layerStack.push(textLayer);
      selectableLayerIds.push(textLayer.id);
    }
    layers.push(...layerStack);
  }
  const editLayers: AddLayerObject[] = [
    {
      id: 'dropmap-annotation-edit-box-fill',
      type: 'fill',
      source: ANNOTATION_SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'edit-box'] as FilterSpecification,
      paint: { 'fill-color': UI_ACCENT_COLOUR, 'fill-opacity': 0.04 },
    },
    {
      id: 'dropmap-annotation-edit-box-outline',
      type: 'line',
      source: ANNOTATION_SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'edit-box'] as FilterSpecification,
      paint: { 'line-color': UI_ACCENT_COLOUR, 'line-width': 2, 'line-dasharray': [3, 2] },
    },
    {
      id: 'dropmap-annotation-edit-outline',
      type: 'line',
      source: ANNOTATION_SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'edit-outline'] as FilterSpecification,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': UI_ACCENT_COLOUR, 'line-width': 4, 'line-opacity': 0.7 },
    },
    {
      id: 'dropmap-annotation-edit-point',
      type: 'circle',
      source: ANNOTATION_SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'edit-point'] as FilterSpecification,
      paint: {
        'circle-radius': ['+', ['/', ['get', 'size'], 2], 5],
        'circle-color': '#ffffff',
        'circle-opacity': 0.15,
        'circle-stroke-color': UI_ACCENT_COLOUR,
        'circle-stroke-width': 2,
      },
    },
    {
      id: 'dropmap-annotation-midpoint-handles',
      type: 'circle',
      source: ANNOTATION_SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'midpoint-handle'] as FilterSpecification,
      paint: {
        'circle-radius': 3.5,
        'circle-color': '#ffffff',
        'circle-stroke-color': UI_ACCENT_COLOUR,
        'circle-stroke-width': 1.5,
      },
    },
    {
      id: 'dropmap-annotation-edit-handles',
      type: 'circle',
      source: ANNOTATION_SOURCE_ID,
      filter: [
        'match',
        ['get', 'kind'],
        [
          'vertex-handle',
          'target-handle',
          'text-position-handle',
          'text-resize-handle',
          'text-height-handle',
          'text-size-handle',
        ],
        true,
        false,
      ] as FilterSpecification,
      paint: {
        'circle-radius': 6,
        'circle-color': '#ffffff',
        'circle-stroke-color': UI_ACCENT_COLOUR,
        'circle-stroke-width': 2,
      },
    },
  ];
  if (annotationLayers.some((layer) => layer.visible)) {
    layers.push(...editLayers);
    selectableLayerIds.push(...editLayers.map((layer) => layer.id));
  }
  const sources = [
    ...(focusData.features.length
      ? [
          {
            id: FOCUS_AREA_SOURCE_ID,
            data: focusData,
            definition: {
              type: 'geojson',
              data: focusData,
              generateId: true,
            } as SourceSpecification,
          },
        ]
      : []),
    ...(annotationLayers.some((layer) => layer.visible)
      ? [
          {
            id: ANNOTATION_SOURCE_ID,
            data,
            definition: {
              type: 'geojson',
              data,
              generateId: true,
              // Geometry is already bounded and screen-space tessellated.
              tolerance: 0,
              maxzoom: 24,
            } as SourceSpecification,
          },
        ]
      : []),
  ];
  return {
    sources,
    images: [...new Map(images.map((image) => [image.id, image])).values()],
    focusBasemapLayers,
    focusDataLayers,
    layers,
    selectableLayerIds,
  };
}
